/** Resumo do processo por IA (beta): linha do tempo, pontos de atenção e próximos passos. Sempre conferir com os autos. */
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Badge, Card, ErrorBox, useAct } from "./kit";

interface Summary {
  resumo?: string; fase?: string; texto?: string;
  linhaDoTempo?: Array<{ data: string; evento: string; importancia?: "alta" | "media" | "baixa" }>;
  pontosDeAtencao?: string[]; proximosPassos?: string[];
}
interface R { available: boolean; summary: Summary | null; at: string | null; stale: boolean; model: string }

export function ProcessAiCard({ processId }: { processId: string }): JSX.Element | null {
  const key = ["processo-resumo-ia", processId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api.get<R>(`/processos/${processId}/resumo-ia`) });
  const run = useAct(() => api.post<R>(`/processos/${processId}/resumo-ia`), [key], undefined, "Resumo do processo gerado");
  if (!data || (!data.available && !data.summary)) return null;
  const s = data.summary;
  return (
    <Card title={<>Resumo do processo por IA <Badge tone="info">beta</Badge></>} actions={data.available &&
      <button className="btn small" disabled={run.isPending} onClick={() => run.mutate()}>{run.isPending ? "Lendo o processo…" : s ? "Refazer" : "Resumir o processo"}</button>}>
      <ErrorBox error={run.error} />
      {!s ? <div className="muted small">Lê as movimentações e intimações e organiza: resumo, fase, linha do tempo, pontos de atenção e próximos passos.</div> : (
        <div className="stack gap-8">
          {data.stale && <div className="banner-warn small">Há movimentações novas depois deste resumo. Clique em "Refazer".</div>}
          <div>{s.fase && <Badge tone="info">{s.fase}</Badge>} {s.resumo ?? s.texto}</div>
          {!!s.pontosDeAtencao?.length && <div><b className="small">Pontos de atenção</b><ul className="ai-list">{s.pontosDeAtencao.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
          {!!s.proximosPassos?.length && <div><b className="small">Próximos passos</b><ul className="ai-list">{s.proximosPassos.map((x, i) => <li key={i}>{x}</li>)}</ul></div>}
          {!!s.linhaDoTempo?.length && <details><summary className="small"><b>Linha do tempo resumida</b> ({s.linhaDoTempo.length})</summary>
            <ul className="portal-timeline">{s.linhaDoTempo.map((e, i) => (
              <li key={i}><span className="muted small">{/^\d{4}-\d{2}-\d{2}/.test(e.data) ? fmtDate(e.data) : e.data}</span>
                <div>{e.importancia === "alta" ? <b>{e.evento}</b> : e.evento}</div></li>
            ))}</ul></details>}
          <div className="muted small">IA pode errar: confira com os autos. {data.at ? `Gerado em ${fmtDateTime(data.at)}` : ""} · {data.model}</div>
        </div>
      )}
    </Card>
  );
}
